import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { clone as cloneSkinned } from "three/examples/jsm/utils/SkeletonUtils.js";
import { ARENA_WORLD_W, ART_FLOOR, ART_H, ART_SCALE, ART_W, SCREEN_H, SCREEN_W, WORLD_TO_ART } from "../shared/screen";
import { DustSwarm, type DustJump } from "./dust3d";
import { Foundry3D } from "./foundry3d";
import { applyOverlay, type OverlayWeights } from "./poseOverlay";
import { ContactShadows } from "./contactShadow";
import { Shot3D, type ShotView } from "./shot3d";
import { PostFx } from "./postFx";
import { blendFrames, type PoseKind } from "./blendTable";
import { DecisiveFx3D, type RingView } from "./decisiveFx3d";
import type { Shot } from "./sprites/animation";

/**
 * Живое 3D (ADR 0003, Джон 04.11.2026: «я просто уже не очень хочу пиксельную игру»): арена и бойцы — three.js.
 * Модель Mixamo со скелетом, клипы боя — действия той же модели; позу задаёт кадр мира (Shot: клип и кадр 30 к/с),
 * своего времени у сцены нет — откат, онлайн и повтор показывают одно и то же.
 *
 * Единица сцены — пиксель мира. Камера подобрана так, что линия боя (z = 0) ложится на экран как прежняя раскладка:
 * 2,5 точки экрана на пиксель мира, пол — на строке ART_FLOOR × 2. Поэтому эффекты прежнего слоя совпадают с бойцами.
 */
const SCREEN_PER_WORLD = WORLD_TO_ART * ART_SCALE;
/** Рост манекена в пикселях мира (как зона поражения стоя). */
const FIGHTER_HEIGHT = 158;
const FOV = 30;
/** Поворот модели к зрителю: удар идёт поперёк кадра, рука видна (как −35° у спрайтов). */
const TURN_TO_VIEWER = THREE.MathUtils.degToRad(20);
// читаемость (контраст к фону ≥ 3:1): светлые тела, тёмные суставы для формы, свой слабый свет тела
const SIDE_COLORS = [
  { body: 0xa8d2ff, joints: 0x3a5e94, glow: 0x6090ff },
  { body: 0xffb088, joints: 0x763a26, glow: 0xe0683a },
] as const;

export interface FighterPose extends OverlayWeights {
  readonly shot: Shot;
  /** Положение стоп, пиксели мира. */
  readonly x: number;
  readonly y: number;
  /** Куда смотрит: 1 — вправо, −1 — влево (зеркальный кадр — уже учтён вызывающим). */
  readonly facing: number;
  /** Не рисовать (тело — облако частиц скачка). */
  readonly hidden: boolean;
  /** Кадр мира и сколько кадров боец в нынешнем состоянии — для смешивания клипов. */
  readonly tick: number;
  readonly since: number;
  /**
   * Поворот тела поверх клипа, радианы (render/koBody.ts): spin — вокруг вертикали (разворот в отлёте), tilt —
   * навзничь от стоп (отлёт кончается лёжа).
   */
  readonly spin?: number;
  readonly tilt?: number;
  /** Довод до касания: на сколько подать модель вперёд в окне удара, пиксели мира (render/reachLean.ts). */
  readonly lean?: number;
  /** Скорость скольжения, px/кадр — для полос от подошв, пока держит удар. */
  readonly slide: number;
  /** Род позы: длина перехода — по паре «откуда → куда» (render/blendTable.ts). */
  readonly kind: PoseKind;
  /**
   * Невидимка 0..1 (консультант 04.11.2026: «модель почти исчезает — прозрачность и мерцающий силуэт-преломление, тень
   * пропадает, у самого игрока лёгкий контур»): насколько спрятан; cloakEdge — сила мерцающего контура (себе — ярче).
   */
  readonly cloak: number;
  readonly cloakEdge: number;
  /**
   * Пара клипов броска: обе модели — в общем пространстве пары, его начало — где бросающий стоял (пиксели мира);
   * facing — взгляд бросающего. Бёдра клипов живые: соперник перелетает через бросающего, как снято.
   */
  readonly paired?: { readonly originX: number; readonly facing: number };
}

/** Полосы от подошв на полу: столько отрезков держим, дальше старые уходят. */
const MAX_MARKS = 240;

interface Fighter3D {
  readonly root: THREE.Object3D;
  readonly mixer: THREE.AnimationMixer;
  current: THREE.AnimationAction | null;
  /** Прежний клип, застывший на последнем кадре, и кадр мира, когда сменился, — для перехода. */
  previous: THREE.AnimationAction | null;
  switchTick: number;
  /** Сколько кадров боец был в состоянии на прошлом кадре: уменьшилось на том же клипе — начался новый удар. */
  since: number;
  /** Скелет внутри модели и его покой: разворот тела — поворот скелета, а не корня (переходы корень смешивают). */
  readonly body: THREE.Object3D | null;
  readonly bodyRest: THREE.Quaternion;
  /** Поворот тела сейчас и в миг смены клипа: гаснет и растёт тем же весом, что смешивание клипов. */
  turnNow: { spin: number; tilt: number };
  turnFrom: { spin: number; tilt: number };
  /** Довод до касания сейчас, пиксели мира вперёд по взгляду. */
  lean: number;
  /** Род позы сейчас и длина текущего перехода, кадров мира. */
  kind: PoseKind;
  blend: number;
  /** Где стояла модель в миг смены клипа: место и поворот плывут к новым за время перехода. */
  rootFrom: { x: number; y: number; angle: number } | null;
  /** Рой скачка-пыльцы из точек поверхности этой модели. */
  swarm: DustSwarm | null;
  /** Пятна касания под стопами. */
  shadows: ContactShadows;
}

/**
 * Киношный свет (render/postFx.ts, мягкие тени) — пока по ?light=1: выкладывается отдельной ступенью после решающего
 * удара (консультант 05.11.2026, порядок).
 */
const LIGHT_PARAM = typeof location !== "undefined" ? new URLSearchParams(location.search).get("light") : null;
const CINEMATIC_LIGHT = LIGHT_PARAM === "1" || LIGHT_PARAM === "2";
/** Мягкие тени VSM — дорогие (размытие карты теней каждый кадр): только при ?light=2 — для замера. */
const SOFT_SHADOWS = LIGHT_PARAM === "2";

/** Ракурс решающего удара: расстояние до точки удара, высота глаза и взгляда, пиксели мира. */
/** Довод до касания: ход за кадр вперёд и назад, пиксели мира — ниже порога рывка (9). */
const LEAN_IN = 5;
const LEAN_OUT = 2;

/** Оси поворота тела (FighterPose.spin, tilt): вертикаль и поперечная — модель смотрит вдоль своей +Z. */
const UP = new THREE.Vector3(0, 1, 0);
const ACROSS = new THREE.Vector3(1, 0, 0);
const spinTurn = new THREE.Quaternion();
const tiltTurn = new THREE.Quaternion();
/** Скачок времени клипа больше этого при смене состояния — новый кусок клипа, переход смешивает (actionFor). */
const CLIP_JUMP_SECONDS = 2 / 30;
const ORBIT_DISTANCE = 400;
const ORBIT_EYE_HEIGHT = 70;
const ORBIT_LOOK_HEIGHT = 85;

/** Высота груди бойца, пиксели мира: туда смотрит камера на наезде заставки. */
const CHEST_HEIGHT = 105;

/** Кости для замера рывков: корпус, голова, руки, ноги. */
const PROBE_BONES = [
  "mixamorigHips",
  "mixamorigSpine1",
  "mixamorigSpine2",
  "mixamorigHead",
  "mixamorigLeftArm",
  "mixamorigRightArm",
  "mixamorigLeftForeArm",
  "mixamorigRightForeArm",
  "mixamorigLeftUpLeg",
  "mixamorigRightUpLeg",
  "mixamorigLeftLeg",
  "mixamorigRightLeg",
] as const;

/** Кости, на которые встают эффекты тела: кисти и стопы. */
const LIMBS = ["mixamorigRightHand", "mixamorigLeftHand", "mixamorigRightToeBase", "mixamorigLeftToeBase"] as const;
/** Чем бьют: кисти, стопы, локти (ForeArm — сустав локтя) и колени (Leg — сустав колена) — для обмера и искр. */
const STRIKERS = [
  ...LIMBS,
  "mixamorigRightForeArm",
  "mixamorigLeftForeArm",
  "mixamorigRightLeg",
  "mixamorigLeftLeg",
] as const;
/** Тело соперника отрезками между суставами: удар касается тела, а не только суставов (и блок — руками). */
const TOUCH_SEGMENTS = [
  ["mixamorigHips", "mixamorigSpine2"],
  ["mixamorigSpine2", "mixamorigHead"],
  ["mixamorigHead", "mixamorigHeadTop_End"],
  ["mixamorigLeftArm", "mixamorigLeftForeArm"],
  ["mixamorigLeftForeArm", "mixamorigLeftHand"],
  ["mixamorigRightArm", "mixamorigRightForeArm"],
  ["mixamorigRightForeArm", "mixamorigRightHand"],
  ["mixamorigLeftUpLeg", "mixamorigLeftLeg"],
  ["mixamorigLeftLeg", "mixamorigLeftFoot"],
  ["mixamorigRightUpLeg", "mixamorigRightLeg"],
  ["mixamorigRightLeg", "mixamorigRightFoot"],
] as const;

/** Слои масок бойцов для замера контраста (1 — первый, 2 — второй). */
const MASK_LAYER = 1;

/** Сколько точек поверхности берём в рой скачка. */
const SWARM_POINTS = 2400;

export class Stage3D {
  readonly canvas: HTMLCanvasElement;
  readonly ready: Promise<void>;
  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene = new THREE.Scene();
  private readonly camera: THREE.PerspectiveCamera;
  private readonly fighters: Fighter3D[] = [];
  private clips = new Map<string, THREE.AnimationClip>();
  private foundry: Foundry3D | null = null;
  /** Киношный свет: свечение ярких мест, грейдинг, виньетка (render/postFx.ts). */
  private readonly post: PostFx | null;
  /** Снаряды бойцов: по одному на бойца. */
  private readonly shots: Shot3D[] = [];
  /** След на конечностях и кольцо волны в замедлении решающего удара. */
  private readonly decisiveFx: DecisiveFx3D;
  /** Камера боя: высота и расстояние без наезда. */
  private baseY = 0;
  private baseDistance = 1;

  constructor() {
    this.canvas = document.createElement("canvas");
    this.canvas.width = SCREEN_W;
    this.canvas.height = SCREEN_H;
    this.renderer = new THREE.WebGLRenderer({ canvas: this.canvas, antialias: true, preserveDrawingBuffer: true });
    this.renderer.setSize(SCREEN_W, SCREEN_H, false);
    this.renderer.shadowMap.enabled = true;
    // мягкие тени (VSM размывает край тени): свет не режет фигуры, как трафарет
    this.renderer.shadowMap.type = CINEMATIC_LIGHT && SOFT_SHADOWS ? THREE.VSMShadowMap : THREE.PCFShadowMap;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.NeutralToneMapping; // цвет бойцов не выцветает, как у ACES

    // камера: на расстоянии, где линия боя шириной в экран мира, взгляд горизонтально на середину экрана
    const visibleHeight = SCREEN_H / SCREEN_PER_WORLD;
    const distance = visibleHeight / 2 / Math.tan(THREE.MathUtils.degToRad(FOV / 2));
    this.camera = new THREE.PerspectiveCamera(FOV, SCREEN_W / SCREEN_H, 10, 4000);
    this.camera.position.set(0, (ART_FLOOR * ART_SCALE - SCREEN_H / 2) / SCREEN_PER_WORLD, distance);
    this.baseY = this.camera.position.y;
    this.baseDistance = distance;

    this.buildArena();
    this.shots.push(new Shot3D(this.scene), new Shot3D(this.scene));
    this.decisiveFx = new DecisiveFx3D(this.scene);
    this.post = CINEMATIC_LIGHT ? new PostFx(this.renderer, this.scene, this.camera, SCREEN_W, SCREEN_H) : null;
    this.ready = this.load();
  }

  private buildArena(): void {
    // свет арены: тёплый ключ сверху-спереди с тенями, холодный контровой сзади — бойцы отделены от фона
    this.scene.add(new THREE.HemisphereLight(0x9a90a8, 0x0a0606, 1.3));
    // ключевой свет — тёплый, сбоку от горна: свет и тень лепят тела, а не заливают их ровно
    const key = new THREE.DirectionalLight(0xffd6a8, 3.4);
    key.position.set(-420, 460, 300);
    key.castShadow = true;
    key.shadow.mapSize.set(2048, 2048);
    key.shadow.radius = 6;
    key.shadow.blurSamples = 12;
    const box = key.shadow.camera;
    box.left = -700;
    box.right = 700;
    box.top = 500;
    box.bottom = -200;
    box.near = 10;
    box.far = 2000;
    this.scene.add(key);
    this.scene.add(key.target);
    const rim = new THREE.DirectionalLight(0xbfd8ff, 3.4);
    rim.position.set(200, 300, -500);
    this.scene.add(rim);
    // Литейня: стена, колонны, канал металла с горячим светом, плиты пола, пар и дым
    this.foundry = new Foundry3D(this.scene, ARENA_WORLD_W / 2);
  }

  /** Ручки невидимки каждого бойца: по набору на материал (render — сила, контур, время мерцания). */
  private readonly cloaks: {
    cloak: { value: number };
    cloakEdge: { value: number };
    cloakTime: { value: number };
  }[][] = [[], []];

  /** Вторые экземпляры клипов — для нового удара из того же клипа (actionFor). */
  private readonly twins = new Map<string, THREE.AnimationClip>();

  private async load(): Promise<void> {
    const cloaks = this.cloaks;
    const gltf = await new GLTFLoader().loadAsync("models/ybot.glb");
    this.clips = new Map(
      gltf.animations.map((clip) => [clip.name, PAIR_CLIPS.has(clip.name) ? alignPair(clip) : inPlace(clip)]),
    );
    const size = new THREE.Box3().setFromObject(gltf.scene).getSize(new THREE.Vector3());
    const scale = FIGHTER_HEIGHT / Math.max(1e-3, size.y);
    for (const side of [0, 1] as const) {
      const root = cloneSkinned(gltf.scene);
      root.scale.setScalar(scale);
      root.traverse((node) => {
        if (!(node instanceof THREE.Mesh)) return;
        node.castShadow = true;
        node.receiveShadow = true;
        node.layers.enable(MASK_LAYER + side); // для замера контраста: маска бойца отдельным проходом
        const colors = SIDE_COLORS[side];
        const joints = node.name.includes("Joints");
        const material = new THREE.MeshStandardMaterial({
          color: joints ? colors.joints : colors.body,
          emissive: joints ? 0x000000 : colors.glow,
          roughness: 0.4,
          metalness: 0.25,
        });
        // светящийся край силуэта (френель): боец отделён от тёмной арены — контраст ≥ 3:1
        const rim = new THREE.Color(joints ? colors.glow : colors.body);
        // невидимка: прозрачность и мерцающий край поверх обычного света; без неё шейдер рисует как прежде
        const cloak = { value: 0 };
        const cloakEdge = { value: 0.5 };
        const cloakTime = { value: 0 };
        cloaks[side]?.push({ cloak, cloakEdge, cloakTime });
        material.transparent = true;
        material.onBeforeCompile = (shader) => {
          shader.uniforms.rimColor = { value: rim };
          shader.uniforms.cloak = cloak;
          shader.uniforms.cloakEdge = cloakEdge;
          shader.uniforms.cloakTime = cloakTime;
          shader.fragmentShader = shader.fragmentShader
            .replace(
              "#include <common>",
              "#include <common>\nuniform vec3 rimColor;\nuniform float cloak;\nuniform float cloakEdge;\nuniform float cloakTime;",
            )
            .replace(
              "#include <emissivemap_fragment>",
              "#include <emissivemap_fragment>\n  float rimK = pow(1.0 - saturate(dot(normal, normalize(vViewPosition))), 2.2);\n  totalEmissiveRadiance += rimColor * rimK * 2.4;",
            )
            .replace(
              "#include <opaque_fragment>",
              "#include <opaque_fragment>\n  if (cloak > 0.0) {\n    float edge = pow(1.0 - saturate(dot(normal, normalize(vViewPosition))), 2.5);\n    float wave = 0.55 + 0.45 * sin(vViewPosition.y * 0.08 + vViewPosition.x * 0.05 + cloakTime * 0.25);\n    gl_FragColor.rgb = mix(gl_FragColor.rgb, rimColor * (0.6 + 0.8 * wave), cloak);\n    gl_FragColor.a = mix(1.0, 0.02 + edge * cloakEdge * wave, cloak);\n  }",
            );
        };
        node.material = material;
      });
      this.scene.add(root);
      const fighter: Fighter3D = {
        root,
        mixer: new THREE.AnimationMixer(root),
        current: null,
        previous: null,
        switchTick: 0,
        since: 0,
        body: root.getObjectByName("Armature") ?? null,
        bodyRest: (root.getObjectByName("Armature")?.quaternion ?? new THREE.Quaternion()).clone(),
        turnNow: { spin: 0, tilt: 0 },
        turnFrom: { spin: 0, tilt: 0 },
        lean: 0,
        kind: "idle",
        blend: 1,
        rootFrom: null,
        swarm: null,
        shadows: new ContactShadows(this.scene),
      };
      this.fighters.push(fighter);
      const swarm = new DustSwarm(this.surfacePoints(fighter), new THREE.Color(SIDE_COLORS[side].body));
      fighter.swarm = swarm;
      this.scene.add(swarm.points);
    }
  }

  /** Точки поверхности модели в позе стойки, в системе бойца: стопы в нуле, лицом в +Z (до поворота). */
  private surfacePoints(fighter: Fighter3D): Float32Array {
    const idle = this.clips.get("fighting-idle");
    if (idle) {
      const action = fighter.mixer.clipAction(idle);
      action.play();
      action.time = 0;
      fighter.mixer.update(0);
      action.stop();
    }
    fighter.root.position.set(0, 0, 0);
    fighter.root.rotation.set(0, 0, 0);
    fighter.root.updateMatrixWorld(true);
    const meshes: THREE.SkinnedMesh[] = [];
    fighter.root.traverse((node) => {
      if (node instanceof THREE.SkinnedMesh) meshes.push(node as THREE.SkinnedMesh);
    });
    const total = meshes.reduce((sum, mesh) => sum + mesh.geometry.getAttribute("position").count, 0);
    const step = Math.max(1, Math.floor(total / SWARM_POINTS));
    const out: number[] = [];
    const vertex = new THREE.Vector3();
    let seen = 0;
    for (const mesh of meshes) {
      const count = mesh.geometry.getAttribute("position").count;
      for (let index = 0; index < count; index++, seen++) {
        if (seen % step !== 0) continue;
        mesh.getVertexPosition(index, vertex);
        mesh.localToWorld(vertex);
        out.push(vertex.x, vertex.y, vertex.z);
      }
    }
    return new Float32Array(out);
  }

  private readonly markMaterial = new THREE.MeshBasicMaterial({
    color: 0x050304,
    transparent: true,
    opacity: 0.55,
    depthWrite: false,
  });
  private readonly markList: THREE.Mesh[] = [];
  private readonly lastFeet: (THREE.Vector3[] | null)[] = [null, null];

  /** Полосы от подошв: пока боец скользит, держа удар, — тёмные отрезки по полу от прежнего места стопы к новому. */
  private marks(fighter: Fighter3D, pose: FighterPose, side: number): void {
    const feet = ["mixamorigLeftToeBase", "mixamorigRightToeBase"].map((name) => {
      const node = fighter.root.getObjectByName(name);
      const point = new THREE.Vector3();
      node?.getWorldPosition(point);
      return point;
    });
    const last = this.lastFeet[side];
    this.lastFeet[side] = feet;
    if (!last || pose.brace <= 0 || Math.abs(pose.slide) < 1 || !fighter.root.visible) return;
    feet.forEach((foot, index) => {
      const from = last[index];
      if (!from || foot.y > 8) return; // стопа в воздухе — следа нет
      const length = Math.hypot(foot.x - from.x, foot.z - from.z);
      if (length < 0.5) return;
      const mark = new THREE.Mesh(new THREE.PlaneGeometry(length, 5), this.markMaterial);
      mark.rotation.x = -Math.PI / 2;
      mark.rotation.z = -Math.atan2(foot.z - from.z, foot.x - from.x);
      mark.position.set((foot.x + from.x) / 2, 0.6, (foot.z + from.z) / 2);
      this.scene.add(mark);
      this.markList.push(mark);
      if (this.markList.length > MAX_MARKS) {
        const old = this.markList.shift();
        if (old) {
          this.scene.remove(old);
          old.geometry.dispose();
        }
      }
    });
  }

  /** Новый раунд — пол чистый. */
  clearMarks(): void {
    for (const mark of this.markList) {
      this.scene.remove(mark);
      mark.geometry.dispose();
    }
    this.markList.length = 0;
  }

  private maskTarget: THREE.WebGLRenderTarget | null = null;
  private readonly maskMaterial = new THREE.MeshBasicMaterial({ color: 0xffffff });

  /**
   * Маска бойцов для замера контраста (только съёмка): каждый — своим слоем, белым без света и тумана, в кадре
   * width×height. Значение — сторона + 1, как у маски спрайтов (render/contrast.ts). Строки — сверху вниз.
   */
  masks(width: number, height: number): Uint8Array {
    if (!this.maskTarget || this.maskTarget.width !== width)
      this.maskTarget = new THREE.WebGLRenderTarget(width, height);
    const out = new Uint8Array(width * height);
    const pixels = new Uint8Array(width * height * 4);
    const background = this.scene.background;
    const fog = this.scene.fog;
    this.scene.background = null;
    this.scene.fog = null;
    this.scene.overrideMaterial = this.maskMaterial;
    for (const side of [0, 1]) {
      this.camera.layers.set(MASK_LAYER + side);
      this.renderer.setRenderTarget(this.maskTarget);
      this.renderer.setClearColor(0x000000, 1);
      this.renderer.clear();
      this.renderer.render(this.scene, this.camera);
      this.renderer.readRenderTargetPixels(this.maskTarget, 0, 0, width, height, pixels);
      for (let y = 0; y < height; y++)
        for (let x = 0; x < width; x++)
          if ((pixels[((height - 1 - y) * width + x) * 4] ?? 0) > 127) out[y * width + x] = side + 1;
    }
    this.renderer.setRenderTarget(null);
    this.camera.layers.set(0);
    this.scene.overrideMaterial = null;
    this.scene.background = background;
    this.scene.fog = fog;
    return out;
  }

  /** Кадр сцены: позы бойцов по кадру мира, камера по камере боя (левый край кадра в пикселях арта). */
  private cameraLeftArt = 0;

  /** Довод до касания: модель идёт к нужной подаче ровным ходом — вперёд не быстрее LEAN_IN, назад LEAN_OUT за кадр. */
  private reachTouch(fighter: Fighter3D, pose: FighterPose): void {
    const target = pose.lean ?? 0;
    const step = target > fighter.lean ? LEAN_IN : LEAN_OUT;
    fighter.lean += Math.max(-step, Math.min(step, target - fighter.lean));
    fighter.root.position.x += fighter.lean * pose.facing;
  }

  /** Поворот скелета поверх клипа: к позе — весом смешивания клипов, от поворота в миг смены клипа. */
  private turnBody(fighter: Fighter3D, pose: FighterPose, weight: number): void {
    const from = fighter.turnFrom;
    const spin = from.spin + ((pose.spin ?? 0) - from.spin) * weight;
    const tilt = from.tilt + ((pose.tilt ?? 0) - from.tilt) * weight;
    fighter.turnNow = { spin, tilt };
    if (!fighter.body) return;
    fighter.body.quaternion
      .copy(fighter.bodyRest)
      .premultiply(tiltTurn.setFromAxisAngle(ACROSS, -tilt))
      .premultiply(spinTurn.setFromAxisAngle(UP, spin));
  }

  /**
   * Клип для позы. Разные состояния берут куски одного клипа (прямой и крюк — оба из punch-combo, блок и отдача в блоке —
   * из center-block): новое состояние с другого места того же клипа берёт второй его экземпляр — и переход смешивает позы,
   * а не прыгает за кадр. Кроме мига попадания (поза держит удар): удар по тебе виден сразу.
   */
  private actionFor(fighter: Fighter3D, clip: THREE.AnimationClip, pose: FighterPose): THREE.AnimationAction {
    const current = fighter.current;
    if (!current || current.getClip().name !== clip.name) return fighter.mixer.clipAction(clip);
    const jumped = Math.abs(pose.shot.frame / 30 - current.time) > CLIP_JUMP_SECONDS;
    const restart = pose.since < fighter.since && pose.brace === 0 && jumped;
    if (!restart) return current;
    let twin = this.twins.get(clip.name);
    if (!twin) {
      twin = clip.clone();
      this.twins.set(clip.name, twin);
    }
    return fighter.mixer.clipAction(current.getClip() === clip ? twin : clip);
  }

  /**
   * Замер позы для поиска рывков (консультант 05.11.2026: «чтобы рывки ловил код, а не глаз»): у каждого бойца —
   * видна ли модель в кадре, где бёдра (пиксели мира) и повороты ключевых костей в мире (кватернионы).
   */
  poseProbe(): { visible: boolean; hips: number[]; bones: number[][] }[] {
    const point = new THREE.Vector3();
    const turn = new THREE.Quaternion();
    return this.fighters.map((fighter) => {
      const hips = fighter.root.getObjectByName("mixamorigHips");
      hips?.getWorldPosition(point);
      const onScreen = point.clone().project(this.camera);
      const visible = fighter.root.visible && Math.abs(onScreen.x) <= 1.1 && Math.abs(onScreen.y) <= 1.1;
      const bones = PROBE_BONES.map((name) => {
        fighter.root.getObjectByName(name)?.getWorldQuaternion(turn);
        return [turn.x, turn.y, turn.z, turn.w];
      });
      return { visible, hips: [point.x, point.y, point.z], bones };
    });
  }
  private readonly probe = new THREE.Vector3();

  /**
   * Кисть или стопа бойца side, ближайшая к точке (x, y) в сетке арта всей арены, — там, где модель в последнем кадре.
   * Эффекты на теле (заряд, искры удара) встают на кость, а не в точку 2D-раскладки. null — бойца не видно.
   */
  /**
   * Точка касания удара на кадре (консультант 05.11.2026: «искры и вспышка — в точке касания: кость кисти или стопы,
   * которой бьют, по позе отрисовки»): из кистей и стоп бьющего — та, что ближе всех к телу соперника (голова, корпус,
   * руки, ноги — блок тоже касание). Точка — в сетке арта по нынешней камере. null — модели нет.
   */
  strikePoint(side: number): { x: number; y: number; bone: string; touch: number } | null {
    const fighter = this.fighters[side];
    const other = this.fighters[side === 0 ? 1 : 0];
    if (!fighter?.root.visible || !other) return null;
    const body = TOUCH_SEGMENTS.flatMap(([from, to]) => {
      const a = other.root.getObjectByName(from);
      const b = other.root.getObjectByName(to);
      return a && b
        ? [new THREE.Line3(a.getWorldPosition(new THREE.Vector3()), b.getWorldPosition(new THREE.Vector3()))]
        : [];
    });
    const nearest = new THREE.Vector3();
    let best: { bone: string; point: THREE.Vector3; touch: number } | null = null;
    for (const name of STRIKERS) {
      const limb = fighter.root.getObjectByName(name);
      if (!limb) continue;
      const point = limb.getWorldPosition(new THREE.Vector3());
      const touch = Math.min(...body.map((part) => part.closestPointToPoint(point, true, nearest).distanceTo(point)));
      if (!best || touch < best.touch) best = { bone: name, point, touch };
    }
    if (!best) return null;
    const art = (point: THREE.Vector3): THREE.Vector2 => {
      const screen = point.clone().project(this.camera);
      return new THREE.Vector2(((screen.x + 1) / 2) * ART_W + this.cameraLeftArt, ((1 - screen.y) / 2) * ART_H);
    };
    const tip = art(best.point);
    // касание — как видит игрок: в плоскости кадра (глубину поворота к зрителю не считаем), пиксели мира
    const view = this.camera.getWorldDirection(new THREE.Vector3());
    const flat = (point: THREE.Vector3): THREE.Vector3 => point.clone().addScaledVector(view, -point.dot(view));
    const limb = flat(best.point);
    const inFrame = Math.min(
      ...body.map((part) =>
        new THREE.Line3(flat(part.start), flat(part.end)).closestPointToPoint(limb, true, nearest).distanceTo(limb),
      ),
    );
    return { x: Math.round(tip.x), y: Math.round(tip.y), bone: best.bone, touch: Math.round(inFrame) };
  }

  /**
   * Обмер клипа (tools/capture/reach.mjs): модель первого бойца на кадре клипа, лицом вправо, стопы в нуле — где кисти
   * и стопы, вперёд и вверх от стоп, пиксели мира. Модель потом снова играет свою позу со следующего кадра.
   */
  measureLimbs(
    clipName: string,
    frame: number,
    bones: readonly string[] = STRIKERS,
    overlay?: OverlayWeights,
  ): Record<string, { forward: number; up: number; depth: number }> | null {
    const fighter = this.fighters[0];
    const clip = this.clips.get(clipName);
    if (!fighter || !clip) return null;
    fighter.mixer.stopAllAction();
    const action = fighter.mixer.clipAction(clip);
    action.reset();
    action.play();
    action.setEffectiveWeight(1);
    action.time = Math.min(clip.duration, frame / 30);
    fighter.root.position.set(0, 0, 0);
    fighter.root.rotation.y = Math.PI / 2 - TURN_TO_VIEWER;
    fighter.body?.quaternion.copy(fighter.bodyRest);
    fighter.mixer.update(0);
    // добавки картинки поверх клипа (откидывание, «держит удар») — обмер видит то же, что игрок
    if (overlay) applyOverlay(fighter.root, overlay);
    fighter.root.updateMatrixWorld(true);
    const out: Record<string, { forward: number; up: number; depth: number }> = {};
    for (const name of bones) {
      const bone = fighter.root.getObjectByName(name);
      if (!bone) continue;
      const point = bone.getWorldPosition(new THREE.Vector3());
      out[name] = {
        forward: Math.round(point.x * 10) / 10,
        up: Math.round(point.y * 10) / 10,
        depth: Math.round(point.z * 10) / 10,
      };
    }
    fighter.current = null;
    fighter.previous = null;
    return out;
  }

  /** Кисти и стопы обоих бойцов в сетке арта — для замера искр (tools/capture/sparks.py). */
  limbsArt(): Record<string, readonly [number, number]>[] {
    return [0, 1].map((side) =>
      Object.fromEntries(
        STRIKERS.flatMap((name) => {
          const point = this.boneArt(side, name);
          return point ? [[name, point] as const] : [];
        }),
      ),
    );
  }

  /** Где кость бойца в сетке арта сейчас — для замера искр (tools/capture). */
  boneArt(side: number, name: string): readonly [number, number] | null {
    const bone = this.fighters[side]?.root.getObjectByName(name);
    if (!bone) return null;
    const screen = bone.getWorldPosition(new THREE.Vector3()).project(this.camera);
    return [((screen.x + 1) / 2) * ART_W + this.cameraLeftArt, ((1 - screen.y) / 2) * ART_H];
  }

  anchor(side: number, x: number, y: number): readonly [number, number] | null {
    const fighter = this.fighters[side];
    if (!fighter?.root.visible) return null;
    let best: readonly [number, number] | null = null;
    let bestDistance = Infinity;
    for (const name of LIMBS) {
      const bone = fighter.root.getObjectByName(name);
      if (!bone) continue;
      bone.getWorldPosition(this.probe).project(this.camera);
      const artX = ((this.probe.x + 1) / 2) * ART_W + this.cameraLeftArt;
      const artY = ((1 - this.probe.y) / 2) * ART_H;
      const distance = (artX - x) ** 2 + (artY - y) ** 2;
      if (distance < bestDistance) {
        bestDistance = distance;
        best = [Math.round(artX), Math.round(artY)];
      }
    }
    return best;
  }

  render(
    poses: readonly FighterPose[],
    cameraLeftArt: number,
    jumps: readonly (DustJump | null)[],
    tick: number,
    view: {
      readonly zoom: number;
      readonly focusX: number;
      readonly lift?: number;
      /** Решающий удар: низкий ракурс 3/4 из-за плеча бьющего, медленный облёт (strength 0..1 — доля к обычному виду). */
      readonly orbit?: {
        strength: number;
        angle: number;
        facing: number;
        attackerX: number;
        /** Кольцо ударной волны решающего удара (render/decisiveFx3d.ts). */
        ring?: RingView | null;
      };
      /** Склейка (новый раунд): модели сразу в новой позе и на новом месте, без перехода. */
      readonly cut?: boolean;
    } = { zoom: 1, focusX: 0 },
    shots: readonly ShotView[] = [],
  ): void {
    shots.forEach((shot, side) => {
      this.shots[side]?.update(shot, tick);
    });
    this.cameraLeftArt = cameraLeftArt;
    this.foundry?.update(tick);
    this.fighters.forEach((fighter, side) => {
      fighter.swarm?.update(jumps[side] ?? null, tick);
    });
    poses.forEach((pose, side) => {
      const fighter = this.fighters[side];
      if (!fighter) return;
      fighter.root.visible = !pose.hidden;
      // где стоит модель: у пары броска — в начале пути пары (клипы выровнены по первому кадру отрезка), иначе — у стоп
      const target = pose.paired
        ? { x: pose.paired.originX, y: 0, angle: pairAngle(pose.paired.facing) }
        : {
            x: pose.x,
            y: -pose.y,
            angle: pose.facing > 0 ? Math.PI / 2 - TURN_TO_VIEWER : -Math.PI / 2 + TURN_TO_VIEWER,
          };
      const clip = this.clips.get(pose.shot.clip);
      if (!clip) return;
      const action = this.actionFor(fighter, clip, pose);
      fighter.since = pose.since;
      if (fighter.current !== action) {
        // переход: прежний клип застывает на своём кадре и гаснет; место и поворот модели плывут вместе с позой —
        // без скачка корня (консультант 05.11.2026: «переход из стойки в бросок без смешивания», «за кадр на полу»)
        fighter.previous?.stop();
        fighter.previous = fighter.current;
        fighter.switchTick = pose.tick;
        fighter.blend = blendFrames(fighter.kind, pose.kind);
        fighter.rootFrom = { x: fighter.root.position.x, y: fighter.root.position.y, angle: fighter.root.rotation.y };
        fighter.turnFrom = { ...fighter.turnNow };
        action.reset();
        action.play();
        fighter.current = action;
      }
      action.time = Math.min(clip.duration, pose.shot.frame / 30);
      fighter.kind = pose.kind;
      // время пошло назад (показ решающего удара из прошлых кадров) — переход считаем завершённым
      if (pose.tick < fighter.switchTick || view.cut) fighter.switchTick = pose.tick - fighter.blend;
      const blend = Math.min(1, Math.max(0, (pose.tick - fighter.switchTick) / fighter.blend));
      placeRoot(fighter, target, fighter.previous ? blend : 1);
      action.setEffectiveWeight(fighter.previous ? blend : 1);
      if (fighter.previous) {
        fighter.previous.setEffectiveWeight(1 - blend);
        if (blend >= 1) {
          fighter.previous.stop();
          fighter.previous = null;
        }
      }
      fighter.mixer.update(0);
      this.turnBody(fighter, pose, fighter.previous ? blend : 1);
      applyOverlay(fighter.root, pose);
      this.reachTouch(fighter, pose);
      for (const handle of this.cloaks[side] ?? []) {
        handle.cloak.value = pose.cloak;
        handle.cloakEdge.value = pose.cloakEdge;
        handle.cloakTime.value = pose.tick;
      }
      // спрятан — тени нет: ни от света, ни пятен касания
      fighter.root.traverse((node) => {
        if (node instanceof THREE.Mesh) node.castShadow = pose.cloak < 0.5;
      });
      this.marks(fighter, pose, side);
      fighter.root.updateMatrixWorld(true);
      fighter.shadows.update(fighter.root, pose.hidden || pose.cloak >= 0.5);
    });
    // камера боя; на заставке суперприёма — наезд на пару (zoom > 1): ближе и ниже, к груди бойцов
    const centerWorld = (cameraLeftArt + ART_W / 2) / WORLD_TO_ART;
    const near = Math.min(1, Math.max(0, (view.zoom - 1) / 1.1));
    const x = centerWorld + (view.focusX - centerWorld) * near;
    const y = this.baseY + (CHEST_HEIGHT + (view.lift ?? 0) * 0.6 - this.baseY) * near;
    this.camera.position.set(x, y, this.baseDistance / view.zoom);
    this.camera.lookAt(x, y, 0);
    const orbit = view.orbit;
    if (orbit && orbit.strength > 0.001) {
      // из-за плеча бьющего: за его спиной и сбоку к зрителю, низко; облёт вокруг точки удара
      const target = new THREE.Vector3(view.focusX, ORBIT_LOOK_HEIGHT, 0);
      const start = orbit.facing > 0 ? Math.PI * 0.82 : Math.PI * 0.18;
      const around = start - orbit.facing * orbit.angle;
      const eye = new THREE.Vector3(
        target.x + Math.cos(around) * ORBIT_DISTANCE,
        ORBIT_EYE_HEIGHT,
        Math.sin(around) * ORBIT_DISTANCE,
      );
      const look = new THREE.Vector3(x, y, 0).lerp(target, orbit.strength);
      this.camera.position.lerp(eye, orbit.strength);
      this.camera.lookAt(look);
    }
    this.decisiveFx.update(
      this.fighters.map((fighter) => fighter.root),
      orbit?.strength ?? 0,
      orbit?.ring ?? null,
      this.camera,
    );
    if (this.post) this.post.render();
    else this.renderer.render(this.scene, this.camera);
  }
}

/** Клип на месте: ход бёдер по полу (x, z) — константа первого ключа, движение по арене задаёт мир. */
/**
 * Пара броска снята лицом в −X: модель поворачивается так, чтобы −X смотрел по взгляду бросающего, с тем же наклоном к
 * зрителю. Бёдра клипов пары выровнены по первому кадру отрезка (alignPair) — модель стоит прямо в начале своего пути.
 */
// строго вдоль линии боя, без наклона к зрителю: путь пары по X ложится на линию боя без сдвига в глубину
const pairAngle = (facing: number): number => (facing > 0 ? Math.PI : 0);
/** Отрезок пары броска начинается с этой секунды клипа (кадр 55 атласа — content/clips.ts, throwAggressor). */
const PAIR_START_TIME = 55 / 30;

/** Бёдра клипа пары — от первого кадра отрезка: в начале броска модель стоит ровно там, где боец. */
function alignPair(clip: THREE.AnimationClip): THREE.AnimationClip {
  for (const track of clip.tracks) {
    if (!track.name.endsWith("Hips.position")) continue;
    const times = track.times;
    const values = track.values;
    let index = times.findIndex((time) => time >= PAIR_START_TIME);
    if (index < 0) index = times.length - 1;
    const x0 = values[index * 3] ?? 0;
    // скелет в GLB повёрнут на 90° вокруг X: компонента 1 — глубина, 2 — высота. Глубину пары убираем совсем: бой идёт
    // по одной линии, иначе в конце броска соперник лежал бы в 90 px от неё и прыгал на неё за кадр
    for (let at = 0; at < values.length; at += 3) {
      values[at] = (values[at] ?? 0) - x0;
      values[at + 1] = 0;
    }
  }
  return clip;
}

/** Дальше этого (пиксели мира) место модели не плывёт, а встаёт сразу. */
const SNAP_DISTANCE = 120;

/** Модель — в новое место за время перехода: от места в миг смены клипа к цели (поворот — кратчайшей дугой). */
function placeRoot(fighter: Fighter3D, target: { x: number; y: number; angle: number }, weight: number): void {
  const from = fighter.rootFrom;
  // скачок места дальше SNAP_DISTANCE — новый раунд или телепорт: смена сцены, модель встаёт сразу, а не плывёт
  if (from && Math.abs(target.x - from.x) > SNAP_DISTANCE) fighter.rootFrom = null;
  if (!fighter.rootFrom || weight >= 1) {
    fighter.root.position.set(target.x, target.y, 0);
    fighter.root.rotation.y = target.angle;
    return;
  }
  const start = fighter.rootFrom;
  const turn = Math.atan2(Math.sin(target.angle - start.angle), Math.cos(target.angle - start.angle));
  fighter.root.position.set(start.x + (target.x - start.x) * weight, start.y + (target.y - start.y) * weight, 0);
  fighter.root.rotation.y = start.angle + turn * weight;
}

/** Клипы пары броска: бёдра живые, но от первого кадра отрезка (alignPair). */
const PAIR_CLIPS: ReadonlySet<string> = new Set(["throw-aggressor", "throw-victim"]);

/**
 * Клипы на месте: бёдра не трогаем — покачивание бёдер держит стопы на полу (Джон 04.11.2026, 22:50: «что с стопами?»;
 * при снятом ходе бёдер стопы в стойке ездили туда-сюда). Так же собраны в GLB (tools/art/build_glb.py, «~»).
 */
const RAW_HIPS: ReadonlySet<string> = new Set([
  "fighting-idle",
  "crouch-idle",
  "center-block",
  "hit-reaction-head",
  "hit-reaction-body",
  "throw-aggressor",
  "throw-victim",
]);

function inPlace(clip: THREE.AnimationClip): THREE.AnimationClip {
  if (RAW_HIPS.has(clip.name)) return clip;
  for (const track of clip.tracks) {
    if (!track.name.endsWith("Hips.position")) continue;
    // скелет в GLB повёрнут на 90° вокруг X: компоненты 0 и 1 — по полу, 2 — высота (её оставляем: присед, падение,
    // подъём идут по высоте клипа). Прежде здесь замораживалась высота — низкие позы висели на высоте первого кадра.
    const values = track.values;
    const x0 = values[0] ?? 0;
    const depth0 = values[1] ?? 0;
    for (let index = 0; index < values.length; index += 3) {
      values[index] = x0;
      values[index + 1] = depth0;
    }
  }
  return clip;
}
